import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Collapse,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Stack,
  TextField,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffOutlinedIcon from '@mui/icons-material/FilterListOffOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageLayout from '../../components/layout/PageLayout';
import DashboardDrilldownBanner from '../../components/dashboard/DashboardDrilldownBanner';
import {
  useDashboardDrilldown,
  useDrilldownFilterSync,
  normalizeFilterKey,
} from '../../hooks/useDashboardDrilldown';
import Can from '../../components/permissions/Can';
import {
  useLazyGetStatusesQuery,
  useLazyGetUsersListQuery,
  useLazyGetServicesQuery,
  useLazyGetLeadsQuery,
  useLazyGetTasksQuery,
  useDeleteTaskMutation,
} from '../../store/api';
import { extractList, extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { SweetAlert } from '../../utils/sweetAlert';
import {
  buildTaskRelationParams,
  leadLabel,
  PRIORITY_OPTIONS,
  serviceLabel,
  taskStatusLabel,
  resolveTaskRowStatusLabel,
  formatTaskListDateTime,
  userLabel,
} from './taskApiHelpers';
import TaskDetailsModal from './TaskDetailsModal';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';

const ellipsize = (v, max = 40) => {
  if (v === undefined || v === null || v === '') return '—';
  const s = String(v);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const priorityColor = (priority) => {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'success';
  return 'default';
};

const fieldSizing = {
  '& .MuiInputBase-root': { borderRadius: 1 },
};

const INITIAL_FILTERS = {
  priority: '',
  status_id: '',
  responsible_id: '',
  service_id: '',
  lead_id: '',
  deadline_from: '',
  deadline_to: '',
};

const TaskList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const {
    active: drilldownActive,
    params: drilldownParams,
    clear: clearDrilldown,
  } = useDashboardDrilldown();
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [detailsTaskId, setDetailsTaskId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filtersDraft, setFiltersDraft] = useState({ ...INITIAL_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...INITIAL_FILTERS });
  const [fetchStatuses] = useLazyGetStatusesQuery();
  const [fetchUsers] = useLazyGetUsersListQuery();
  const [fetchServices] = useLazyGetServicesQuery();
  const [fetchLeads] = useLazyGetLeadsQuery();
  const [fetchTasks] = useLazyGetTasksQuery();
  const [deleteTask] = useDeleteTaskMutation();

  const fetchTaskFilterStatuses = useCallback(async () => {
    try {
      return await fetchStatuses({
        'conditions[model]': 'Task',
        paginate: 0,
      }).unwrap();
    } catch {
      try {
        return await fetchStatuses({
          'conditions[model]': 'task',
          paginate: 0,
        }).unwrap();
      } catch {
        return await fetchStatuses({ paginate: 0 }).unwrap();
      }
    }
  }, [fetchStatuses]);

  const statusLazyTrigger = useCallback(
    () => ({
      unwrap: () => fetchTaskFilterStatuses(),
    }),
    [fetchTaskFilterStatuses]
  );

  const statusSelect = useLazySelectOptions(statusLazyTrigger, {
    mapResult: (res) =>
      extractList(res).map((item) => ({
        id: item?.id,
        label: taskStatusLabel(item),
      })),
  });

  const userSelect = useLazySelectOptions(fetchUsers, {
    args: { paginate: 0 },
    mapResult: (res) =>
      extractList(res)
        .map((item) => ({
          id: item?.id ?? item?.uuid,
          label: userLabel(item),
        }))
        .filter((item) => item.id),
  });

  const serviceSelect = useLazySelectOptions(fetchServices, {
    args: { paginate: 0 },
    mapResult: (res) =>
      (extractList(res) || [])
        .map((item) => ({
          id: item?.id,
          label: serviceLabel(item),
        }))
        .filter((item) => item.id),
  });

  const leadSelect = useLazySelectOptions(fetchLeads, {
    args: { paginate: 0 },
    mapResult: (res) =>
      (extractList(res) || [])
        .map((item) => ({
          id: item?.id,
          label: leadLabel(item),
        }))
        .filter((item) => item.id),
  });

  const statusOptions = statusSelect.options;

  // A task card opened this list: select its status (or today's deadline) in the filters.
  const drilldownMetric = drilldownParams.dd_metric;
  const { load: loadStatusOptions } = statusSelect;
  useEffect(() => {
    if (drilldownMetric && drilldownMetric !== 'today_deadline') {
      loadStatusOptions().catch(() => {});
    }
  }, [drilldownMetric, loadStatusOptions]);

  const drilldownFilters = useMemo(() => {
    if (!drilldownMetric) return undefined;
    if (drilldownMetric === 'today_deadline') {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      return { deadline_from: today, deadline_to: today };
    }
    const key = normalizeFilterKey(drilldownMetric);
    const status = statusOptions.find((s) => normalizeFilterKey(s.label) === key);
    return status ? { status_id: status.id } : undefined;
  }, [drilldownMetric, statusOptions]);

  const detachDrilldown = useDrilldownFilterSync({
    active: drilldownActive,
    value: drilldownFilters,
    apply: (patch) => setAppliedFilters((prev) => ({ ...prev, ...patch })),
    reset: () =>
      setAppliedFilters((prev) => ({ ...prev, status_id: '', deadline_from: '', deadline_to: '' })),
    clear: clearDrilldown,
  });
  const userOptions = userSelect.options;
  const serviceOptions = serviceSelect.options;
  const leadOptions = leadSelect.options;

  const loadFilterStatusOptions = useCallback(() => {
    statusSelect.load().catch(() => toast.error('Could not load statuses'));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [statusSelect.load]);

  const loadFilterUserOptions = useCallback(() => {
    userSelect.load().catch(() => toast.error('Could not load users'));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [userSelect.load]);

  const loadFilterServiceOptions = useCallback(() => {
    serviceSelect.load().catch(() => toast.error('Could not load services'));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [serviceSelect.load]);

  const loadFilterLeadOptions = useCallback(() => {
    leadSelect.load().catch(() => toast.error('Could not load leads'));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [leadSelect.load]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        paginate: 1,
        ...buildTaskRelationParams(),
        ...(search ? { search } : {}),
        ...(appliedFilters.priority ? { 'filters[priority]': appliedFilters.priority } : {}),
        ...(appliedFilters.status_id ? { 'filters[status_id]': appliedFilters.status_id } : {}),
        ...(appliedFilters.responsible_id ? { 'filters[responsible_id]': appliedFilters.responsible_id } : {}),
        ...(appliedFilters.service_id ? { 'filters[service_id]': appliedFilters.service_id } : {}),
        ...(appliedFilters.lead_id ? { 'filters[lead_id]': appliedFilters.lead_id } : {}),
        // Range filters go through date_from/date_to/date_field — `filters[deadline][0..1]`
        // reaches the API as whereIn(deadline, [from, to]), which never matches a range.
        ...(appliedFilters.deadline_from ? { date_from: appliedFilters.deadline_from } : {}),
        ...(appliedFilters.deadline_to ? { date_to: appliedFilters.deadline_to } : {}),
        ...(appliedFilters.deadline_from || appliedFilters.deadline_to
          ? { date_field: 'deadline' }
          : {}),
        ...drilldownParams,
      };

      const data = await fetchTasks(params).unwrap();
      const list = extractList(data);
      setRows(Array.isArray(list) ? list : []);
      const pag = extractPagination(data);
      setPagination((p) => ({
        ...p,
        total: pag.total ?? (Array.isArray(list) ? list.length : 0),
        page: pag.page ?? p.page,
        per_page: pag.per_page ?? p.per_page,
      }));
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to load tasks');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    appliedFilters.deadline_from,
    appliedFilters.deadline_to,
    appliedFilters.lead_id,
    appliedFilters.priority,
    appliedFilters.responsible_id,
    appliedFilters.service_id,
    appliedFilters.status_id,
    pagination.page,
    pagination.per_page,
    search,
    fetchTasks,
    drilldownParams,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleFilterDraftChange = (key, value) => {
    setFiltersDraft((prev) => ({ ...prev, [key]: value ?? '' }));
  };

  const handleDeadlineDraftChange = (key, value) => {
    setFiltersDraft((prev) => {
      const next = { ...prev, [key]: value ?? '' };
      if (key === 'deadline_from' && next.deadline_from && next.deadline_to && next.deadline_from > next.deadline_to) {
        next.deadline_to = next.deadline_from;
      }
      if (key === 'deadline_to' && next.deadline_from && next.deadline_to && next.deadline_to < next.deadline_from) {
        next.deadline_from = next.deadline_to;
      }
      return next;
    });
  };

  const prevShowFiltersRef = React.useRef(false);

  useEffect(() => {
    if (showFilters && !prevShowFiltersRef.current) {
      setFiltersDraft({ ...appliedFilters });
    }
    prevShowFiltersRef.current = showFilters;
  }, [showFilters, appliedFilters]);

  const handleApplyFilters = () => {
    detachDrilldown();
    setPagination((prev) => ({ ...prev, page: 1 }));
    setAppliedFilters({ ...filtersDraft });
  };

  const handleRemoveFilters = () => {
    detachDrilldown();
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFiltersDraft({ ...INITIAL_FILTERS });
    setAppliedFilters({ ...INITIAL_FILTERS });
  };

  const activeFiltersCount = Object.values(appliedFilters).filter(Boolean).length;

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleOpenDetails = useCallback((taskId) => {
    if (!taskId) return;
    setDetailsTaskId(taskId);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setDetailsTaskId(null);
  }, []);

  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/tasks/edit/${id}`);
  };

  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) handleOpenDetails(id);
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();
    const result = await SweetAlert.confirm({
      title: 'Delete task?',
      text: 'This action cannot be undone.',
      confirmButtonText: 'Delete',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;

    setActionLoadingId(row.id);
    try {
      await deleteTask(row.id).unwrap();
      toast.success('Task deleted');
      fetchRows();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete task');
    } finally {
      setActionLoadingId(null);
    }
  };

  const colCount = 11;

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <DashboardDrilldownBanner />
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Tasks
          </Typography>
          <Can permission="task-create">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tasks/create')}>
              Add task
            </Button>
          </Can>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((v) => !v)}
            sx={{
              fontWeight: 600,
              borderWidth: 1.5,
              ...(activeFiltersCount > 0
                ? {
                  borderColor: 'primary.main',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'light'
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.primary.main, 0.22),
                }
                : {}),
            }}
          >
            Filter
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Collapse in={showFilters} timeout="auto" unmountOnExit>
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: (theme) =>
                theme.palette.mode === 'light' ? '0 1px 3px rgba(15,23,42,0.08)' : 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ px: 2.5, py: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                Filters
              </Typography>

              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="flex-end"
                useFlexGap
                spacing={2}
                sx={{ columnGap: 2, rowGap: 2 }}
              >
                <Box sx={{
                  gap: '20px',
                  display: 'flex',
                  flexWrap: 'wrap'
                }}>
<Typography
sx={{marginBottom: '10px',
  width: '100%'
}}
>
  Project Dead Line
</Typography>
                
                  <TextField
                    type="date"
                    size="small"
                    label="Date from"
                    value={filtersDraft.deadline_from}
                    onChange={(e) => handleDeadlineDraftChange('deadline_from', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: filtersDraft.deadline_to || undefined }}
                    sx={{ ...fieldSizing, minWidth: 240  }}
                  />
                  <TextField
                    type="date"
                    size="small"
                    label="Date to"
                    value={filtersDraft.deadline_to}
                    onChange={(e) => handleDeadlineDraftChange('deadline_to', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: filtersDraft.deadline_from || undefined }}
                    sx={{ ...fieldSizing, minWidth: 240 }}
                  />
                </Box>
                <Autocomplete
                  options={userOptions}
                  loading={userSelect.loading}
                  onOpen={loadFilterUserOptions}
                  size="small"
                  sx={{ ...fieldSizing, minWidth: 200, flex: '1 1 180px' }}
                  value={userOptions.find((item) => item.id === filtersDraft.responsible_id) ?? null}
                  onChange={(_, value) => handleFilterDraftChange('responsible_id', value?.id ?? '')}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Responsible Person" placeholder="Responsible Person" size="small" fullWidth />
                  )}
                />

                <FormControl size="small" sx={{ ...fieldSizing, minWidth: 130 }}>
                  <InputLabel id="task-filter-priority-label">Priority</InputLabel>
                  <Select
                    labelId="task-filter-priority-label"
                    label="Priority"
                    value={filtersDraft.priority}
                    onChange={(e) => handleFilterDraftChange('priority', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {PRIORITY_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  options={statusOptions}
                  loading={statusSelect.loading}
                  onOpen={loadFilterStatusOptions}
                  size="small"
                  sx={{ ...fieldSizing, minWidth: 160, flex: '1 1 140px' }}
                  value={statusOptions.find((item) => item.id === filtersDraft.status_id) ?? null}
                  onChange={(_, value) => handleFilterDraftChange('status_id', value?.id ?? '')}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => <TextField {...params} label="Status" size="small" fullWidth />}
                />

                <Autocomplete
                  options={serviceOptions}
                  loading={serviceSelect.loading}
                  onOpen={loadFilterServiceOptions}
                  size="small"
                  sx={{ ...fieldSizing, minWidth: 160, flex: '1 1 140px' }}
                  value={serviceOptions.find((item) => item.id === filtersDraft.service_id) ?? null}
                  onChange={(_, value) => handleFilterDraftChange('service_id', value?.id ?? '')}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => <TextField {...params} label="Service" size="small" fullWidth />}
                />

                <Autocomplete
                  options={leadOptions}
                  loading={leadSelect.loading}
                  onOpen={loadFilterLeadOptions}
                  size="small"
                  sx={{ ...fieldSizing, minWidth: 180, flex: '1 1 160px' }}
                  value={leadOptions.find((item) => item.id === filtersDraft.lead_id) ?? null}
                  onChange={(_, value) => handleFilterDraftChange('lead_id', value?.id ?? '')}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Customer / lead" size="small" fullWidth />
                  )}
                />

                <Button variant="contained" onClick={handleApplyFilters} sx={{ fontWeight: 700, px: 2.5 }}>
                  APPLY
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<FilterListOffOutlinedIcon />}
                  onClick={handleRemoveFilters}
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    borderColor: 'grey.400',
                    color: 'text.secondary',
                  }}
                >
                  REMOVE FILTER
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Collapse>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Reference Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Lead</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Responsible</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Deadline</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created at</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 72, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No tasks found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => handleOpenDetails(row.id)}
                        sx={{ cursor: row.id ? 'pointer' : 'default' }}
                      >
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Tooltip title={row.reference_code ?? '-'}>
                            <Typography variant="body2" noWrap>
                              {ellipsize(row.reference_code, 42)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Tooltip title={row.title ?? ''}>
                            <Typography variant="body2" noWrap>
                              {ellipsize(row.title, 42)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.priority ?? '—'}
                            color={priorityColor(row.priority)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography variant="body2" noWrap title={row.lead?.name ?? ''}>
                            {ellipsize(row.lead?.name ?? row.lead?.dba_name, 28)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography variant="body2" noWrap title={row.service?.name ?? ''}>
                            {ellipsize(row.service?.name, 28)}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.responsible?.name ?? row.responsible?.username ?? '—'}</TableCell>
                      
                        <TableCell>
                          <Chip
                            size="small"
                            label={resolveTaskRowStatusLabel(row) || '—'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatTaskListDateTime(row.deadline)}
                        </TableCell>
                          <TableCell>{row.author?.name ?? '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatTaskListDateTime(row.created_at)}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            disabled={actionLoadingId === row.id}
                            aria-label="Actions"
                          >
                            {actionLoadingId === row.id ? <CircularProgress size={20} /> : <MoreVertIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Can permission="task-view">
          <MenuItem onClick={goView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="task-edit">
          <MenuItem onClick={goEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="task-delete">
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>

      <TaskDetailsModal
        open={detailsOpen}
        taskId={detailsTaskId}
        onClose={handleCloseDetails}
        onUpdated={fetchRows}
      />
    </PageLayout>
  );
};

export default TaskList;
